// Fetches fees, limits, and per-action access status for the current user.
// Call once at the app level or at the top of any trading page.
//
// Returns:
//   config       — the full data object
//   fees         — { buyFeePercent, sellFeePercent, withdrawCryptoFee, billFeePercent }
//   limits       — { minBuyNGN, maxBuyNGN, minSellNGN, dailySellLimitNGN, … }
//   actions      — { sell, buy, bill, withdraw_crypto } each { allowed, code, message }
//   loading      — true on first fetch
//   error        — string | null
//   canDo(action)— shorthand: returns true if that action is allowed
//   refresh()    — manual refetch (call after user completes KYC etc.)

import { useState, useEffect, useCallback, useRef } from "react";
import { getTradingMeta } from "../api/apiServices";

export function useTradingConfig({ enabled = true } = {}) {
  const [config,  setConfig]  = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);
  const initialised           = useRef(false);

  const fetch_ = useCallback(async () => {
    if (!enabled) { setLoading(false); return; }
    if (!initialised.current) setLoading(true);
    setError(null);

    try {
      const res = await getTradingMeta();
      if (res.status) setConfig(res.data);
      else setError(res.message || "Failed to load config");
    } catch (err) {
      setError(err?.message || "Failed to load config");
    } finally {
      setLoading(false);
      initialised.current = true;
    }
  }, [enabled]);

  useEffect(() => { fetch_(); }, [fetch_]);

  const canDo = (action) => config?.actions?.[action]?.allowed ?? true;

  return {
    config,
    fees:    config?.fees    || {},
    limits:  config?.limits  || {},
    actions: config?.actions || {},
    platform: config?.platform || {},
    loading,
    error,
    canDo,
    refresh: fetch_,
  };
}
